import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import App from './App.tsx'
import './index.css'
import './prosemirror.css'
import { queryClient } from './queryClient.ts'

const isDevelopment = import.meta.env.VITE_APP_ENV === 'development'
const isProduction = import.meta.env.VITE_APP_ENV === 'production'

/** Show a visible error instead of white screen when React fails to mount or throws during bootstrap */
function showBootstrapError(message: string, detail?: string) {
    const root = document.getElementById('root')
    if (!root) return
    root.innerHTML = `
    <div style="font-family: system-ui, sans-serif; padding: 2rem; max-width: 600px; margin: 2rem auto;">
      <h1 style="color: #b91c1c;">App failed to load</h1>
      <p style="color: #374151;">${message}</p>
      ${detail ? `<pre style="background: #f3f4f6; padding: 1rem; overflow: auto; font-size: 12px;">${detail}</pre>` : ''}
      <p style="color: #6b7280; font-size: 14px;">Check the browser console (F12) for more details.</p>
    </div>
  `
}

// Catch errors that happen before or outside React so we don't get a blank white screen.
// Only overwrite #root if it's still empty or we already showed a bootstrap error (avoids wiping the app on late rejections).
let bootstrapErrorShown = false
function handleBootstrapError(message: string, detail?: string) {
    console.error('[Bootstrap error]', message, detail)
    if (!bootstrapErrorShown) {
        bootstrapErrorShown = true
        showBootstrapError(message, detail)
    }
}
// Only intercept errors that happen BEFORE React has painted anything.
// Once React has rendered into #root, do NOT overwrite the DOM — that causes
// the insertBefore/removeChild cascade because React's fiber tree is now out of
// sync with the real DOM.
let reactHasPainted = false
window.onerror = (msg, source, lineno, colno, error) => {
    if (reactHasPainted) {
        // Let React's own ErrorBoundary handle it; don't touch the DOM.
        console.error('[Bootstrap error]', String(msg), error?.stack ?? `${source}:${lineno}:${colno}`)
        return false
    }
    handleBootstrapError(String(msg), error?.stack ?? `${source}:${lineno}:${colno}`)
    return false
}
window.onunhandledrejection = (event) => {
    const reason = event.reason
    console.error('[Unhandled rejection]', reason)
    // Only show on first load: root has no children or only one text node (Vite placeholder)
    const root = document.getElementById('root')
    const isLikelyBeforePaint = root && (root.children.length === 0 || (root.children.length === 1 && root.querySelector('style')))
    if (isLikelyBeforePaint) {
        handleBootstrapError('Unhandled promise rejection', reason?.stack ?? String(reason))
    }
}

Sentry.init({
    dsn: 'https://27e35fa62a1b32040679d18bd2f10fff@o4506639852109824.ingest.sentry.io/4506639882321920',
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    // Performance Monitoring - Lower sample rate in production to control costs
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in prod, 100% in staging/dev
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'https://staging.commzone.app/api', 'https://commzone.app/api'],
    // Session Replay - Good balance of coverage vs cost
    replaysSessionSampleRate: isProduction ? 0.05 : 0.1, // 5% in prod, 10% in staging/dev
    replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
    // Only enable in non-development environments
    enabled: !isDevelopment,
    environment: import.meta.env.VITE_APP_ENV,
})

const rootEl = document.getElementById('root')
if (!rootEl) {
    document.body.innerHTML = '<pre style="padding:2rem;color:red;">Fatal: #root element not found.</pre>'
} else {
    try {
        reactHasPainted = true
        ReactDOM.createRoot(rootEl).render(
            <React.StrictMode>
                <BrowserRouter
                    future={{
                        v7_startTransition: true,
                        v7_relativeSplatPath: true,
                    }}
                >
                    <QueryParamProvider adapter={ReactRouter6Adapter}>
                        <QueryClientProvider client={queryClient}>
                            <App />

                            {import.meta.env.VITE_PR_SERVER === 'development' ? (
                                <ReactQueryDevtools initialIsOpen={false} />
                            ) : null}
                        </QueryClientProvider>
                    </QueryParamProvider>
                </BrowserRouter>
            </React.StrictMode>
        )
    } catch (err) {
        console.error('[React mount error]', err)
        showBootstrapError(
            err instanceof Error ? err.message : 'React failed to mount',
            err instanceof Error ? err.stack : String(err)
        )
    }
}
